import styled from 'styled-components'
import Link from 'next/link'
import { useCourse } from 'querys/course'
import { SecondaryButton } from 'common/button'
import { Typography } from 'common/typography'
import Wrapper from 'common/wrapper'
import Divider from 'common/divider'
import ContentRenderer from 'content-renderer'
import useIntersectionObserver from 'hooks/use-intersection-observer'
import Price from '../pricing/server-price'
import LastViewLesson from './last-view-lesson'
import Stars from './stars'
import BadgeComposition from './badge-composition'

const LearningHeroStyled = styled.section`
  margin: 72px 0 80px;
  background-image: ${({ bg }) => bg && `linear-gradient(188.94deg, rgba(4, 0, 30, 0.05) -21.75%, #04001E 56.99%), url(${bg})`};
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
  height: calc(100vh - 72px);
  display: flex;
  align-items: flex-end;
  max-height: 632px;
  h1 {
    text-align: center;
    max-inline-size: 300px;
    margin: 0 auto;
  }
  .learning-badge {
    margin: 0 auto;
    margin-bottom: 16px;
    overflow: visible;
    text-align: center;
    p {
      margin-top: 16px;
    }
  }
  .stars {
    margin: 16px 0;
  }
  .course-summary p {
    font: normal 14px/22px var(--primaryFont);
  }
  .certificate-btn {
    margin-block-start: 1rem;
  }
  @media(min-width: 768px) {
    margin: 72px 0 40px;
    padding: 64px 0 76px;
    display: block;
    height: auto;
    background-image: ${({ bg }) => bg && ` linear-gradient(254.43deg, rgba(4, 0, 30, 0.05) 29.62%, #04001E 58.05%), linear-gradient(188.94deg, rgba(4, 0, 30, 0.05) -21.75%, #04001E 81.99%), radial-gradient(95.43% 324.07% at 74.52% -14.35%, rgba(4, 0, 30, 0) 36.21%, #04001E 100%), radial-gradient(119.24% 584.88% at 0% -13.8%, #04001E 0%, rgba(4, 0, 30, 0) 93.49%), url(${bg})`};
    background-position: right;
    background-size: 1200px 432px, 1200px 432px , 1200px 432px, 1200px 432px, 768px 432px;
    h1 {
      max-inline-size: max-content;
    }
    .details {
      max-inline-size: 650px;
    }
    .learning-hero {
      display: flex;
      align-items: center;
      gap: 2rem;
    }
    .learning-badge {
      order: 2;
      margin: 0 auto;
    }
    h1 {
      text-align: left;
      margin: 0;
    }
  }
  @media(min-width: 1024px) {
    margin: 0 0 40px;
  }
`

function LearningHero() {
  const {
    data: {
      title, slug,
      discount, price, original_price,
      summary: { blocks },
      last_viewed_lesson,
      course_progress,
      has_access,
      is_free,
      user_review: { stars, comment },
      featured_video: { thumbnails },
    },
  } = useCourse()
  const { ref: heroRef } = useIntersectionObserver({
    handleIntersect: (e) => {
      const banner = document.querySelector('#pricing-banner')
      if (!banner) return
      if (e.isIntersecting) {
        banner.classList.add('opacity')
        return
      }
      banner.classList.remove('opacity')
    },
  })
  const bg = last_viewed_lesson?.video?.thumbnails[0]?.link || (thumbnails && thumbnails[0]?.link)
  const progress = course_progress?.progress || 0

  return (
    <LearningHeroStyled bg={bg} ref={heroRef}>
      <Wrapper maxWidth="1200">
        <div className="learning-hero">
          <div className="learning-badge">
            <BadgeComposition />
            {progress ? (
              <Typography font="headline6" color="var(--baseNeutral)">
                {progress}
                {'%'}
              </Typography>
            ) : null}
          </div>
          <div className="details">
            <Typography small="headline5" large="headline4" as="h1" color="var(--baseNeutral)">
              {title}
            </Typography>
            <div className="stars">
              <Stars stars={stars} aling="center" />
            </div>
            {has_access && last_viewed_lesson ? (
              <LastViewLesson lesson={last_viewed_lesson} course={slug} review={comment} />
            ) : (
              <>
                <div className="course-summary">
                  <ContentRenderer chunks={blocks.slice(0, 1)} color="var(--baseNeutral)" />
                </div>
                {
                  !has_access && !is_free && (
                    <>
                      <Price
                        price={price}
                        discount={discount}
                        originalPrice={original_price}
                        limitedTime={false}
                        data-cy="course-pricing"
                      />
                      <Divider marginVertical={12} stroke={1} opacity="1" width="100%" color="transparent" />
                      <Link href={`/comprar/cursos/${slug}`} passHref>
                        <SecondaryButton fullWidth fitContent>
                          Comprar ahora
                        </SecondaryButton>
                      </Link>
                    </>
                  )
                }
                {
                  !has_access && is_free && (
                    <Link href={`/cursos/${slug}`} passHref>
                      <SecondaryButton fullWidth fitContent>
                        Unirme ahora
                      </SecondaryButton>
                    </Link>
                  )
                }
              </>
            )}
          </div>
        </div>
      </Wrapper>
    </LearningHeroStyled>
  )
}

export default LearningHero
